import { useMemo } from 'react';
import { i18n } from 'app/utils/i18n';
import { computeDerivedColors, mix } from '../utils/derived-colors';
import { sanitizeColorValue } from '../utils/scss-formatting';
import './ThemeColorPicker.scss';

const weights = [100, 75, 50, 25, 10] as const;

const colorGroups = [
  { base: 'xrpPrimaryColor', prefix: 'primaryColor', label: 'dc.color.primary' },
  { base: 'xrpSecondaryColor', prefix: 'secondaryColor', label: 'dc.color.secondary' },
  { base: 'xrpTertiaryColor', prefix: 'tertiaryColor', label: 'dc.color.tertiary' },
  { base: 'xrpQuaternaryColor', prefix: 'quaternaryColor', label: 'dc.color.quaternary' },
  { base: 'xrpHeadingColor', prefix: 'headingColor', label: 'dc.color.heading' },
] as const;

interface ThemeColorPickerProps {
  value: string;
  onChange: (hex: string) => void;
  themeColors: Record<string, string>;
}

export function ThemeColorPicker({ value, onChange, themeColors }: ThemeColorPickerProps) {
  const allColors = useMemo(() => {
    const derived = computeDerivedColors(themeColors);

    // Add heading variants (not in computeDerivedColors)
    const heading = themeColors['xrpHeadingColor'] || '#0f1720';
    for (const w of weights) {
      derived[`headingColor${w}`] = mix(heading, '#ffffff', w);
    }

    return derived;
  }, [themeColors]);

  const isSelected = (hex: string) => value && hex && value.toLowerCase() === hex.toLowerCase();

  return (
    <div className="theme-color-picker">
      <div className="theme-color-picker__grid">
        {/* Header row: empty corner + column labels */}
        <div />
        {colorGroups.map((group) => (
          <div key={group.prefix} className="theme-color-picker__column-label">
            {i18n(group.label)}
          </div>
        ))}

        {/* One row per weight */}
        {weights.map((w) => (
          <>
            <div key={`label-${w}`} className="theme-color-picker__weight-label">
              {w}%
            </div>
            {colorGroups.map((group) => {
              const hex = allColors[`${group.prefix}${w}`] || '';
              return (
                <button
                  key={`${group.prefix}-${w}`}
                  type="button"
                  className={`theme-color-picker__swatch ${isSelected(hex) ? 'theme-color-picker__swatch--active' : ''}`}
                  style={{ backgroundColor: hex }}
                  onClick={() => onChange(hex)}
                  title={`${hex} (${w}%)`}
                />
              );
            })}
          </>
        ))}
      </div>

      <div className="theme-color-picker__custom">
        <input type="color" className="theme-color-picker__native-input" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} />
        <input
          type="text"
          className="form-control form-control-sm"
          value={value}
          onChange={(e) => onChange(sanitizeColorValue(e.target.value))}
          placeholder="#000000"
          style={{ width: 120 }}
        />
        {value && (
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onChange('')} title={i18n('dc.modal.color-clear')}>
            <i className="fa-regular fa-ban" />
          </button>
        )}
      </div>
    </div>
  );
}
